/**
 * PHA-1204: upload the care plan to the EHR automatically, right after it is approved.
 *
 * For clinics with "Automatically upload new care plans to EHR" on, approving a new care plan
 * (the end of onboarding, or a completed regeneration) files it into the EHR without anyone
 * pressing "Push Care Plan to EMR".
 *
 * The approval wizard holds only the plan being edited, not the chart's full patient record,
 * so the inputs are re-read from the server here — the same reads the chart makes — and the
 * document is rendered through the same buildCarePlanPdfPayload + generateCarePlanPdfBlob path
 * as the button (PHA-983). The copy filed automatically is therefore the copy a clinician
 * would have filed by hand.
 */
import { patientApi } from '../services/api';
import { buildCarePlanPdfPayload, fetchCarePlanReviewsForPdf } from './carePlanPdfPayload';
import { generateCarePlanPdfBlob } from './generateCarePlanPdf';

/* eslint-disable @typescript-eslint/no-explicit-any */

// PHA-1212: the upload is asynchronous — Cobalt accepts it now and the EHR's verdict arrives by
// webhook within about a minute — so approval must not claim the plan is in the chart yet.
export const AUTO_PUSH_SUBMITTED_TITLE = 'Care Plan Created Successfully';
export const AUTO_PUSH_SUBMITTED_DETAIL =
  'The care plan has been created and submitted to the EHR. The final upload status will ' +
  "appear in the patient's Care Plans tab within approximately one minute.";
export const AUTO_PUSH_FAILURE_TITLE = 'Failed to Upload Care Plan';
export const AUTO_PUSH_FAILURE_DETAIL =
  'The care plan was generated successfully but could not be uploaded to the EHR. ' +
  'Please try again later or contact support if the issue persists.';

export type AutoPushOutcome =
  /** The clinic does not auto-push (or push is not configured) — nothing was attempted. */
  | { status: 'skipped' }
  /** The PDF was accepted by the EHR integration; the EHR's own verdict is still to come. */
  | { status: 'submitted' }
  /** This exact plan version was already in the chart (409). Not a failure. */
  | { status: 'already_sent' }
  | { status: 'failed'; error: unknown };

/**
 * Should this patient's clinic auto-push? Answered by the server from the PATIENT's
 * organization (never the clinic switcher — PHA-959), and it is false whenever the push
 * itself is not fully configured.
 *
 * Retried once: this call is the only thing that decides whether the clinician hears about
 * the upload at all, so a single transient blip would otherwise turn an auto-push clinic's
 * approval into a silent non-upload.
 */
const resolveAutoPushEnabled = async (patientId: string): Promise<boolean | null> => {
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      const status = await patientApi.getCarePlanEhrPushStatus(patientId);
      return status?.auto_push_enabled === true;
    } catch (err) {
      console.error(`PHA-1204: push status fetch failed (attempt ${attempt}):`, err);
    }
  }
  return null;
};

const resolveVoiceOfPatientEnabled = async (orgId: string | null): Promise<boolean> => {
  if (!orgId) return false;
  try {
    const result = await patientApi.getOrgVoiceOfPatientEnabled(orgId);
    return result?.voice_of_patient_enabled === true;
  } catch {
    // Same fallback the chart applies (PatientDetail, PHA-795), so the two copies agree.
    return false;
  }
};

const resolveNoTargetDatesEnabled = async (patient: any, orgId: string | null): Promise<boolean> => {
  // PHA-866: prefer the value delivered with the patient payload, as the chart does.
  if (typeof patient?.no_target_dates_enabled === 'boolean') return patient.no_target_dates_enabled;
  if (!orgId) return false;
  try {
    const result = await patientApi.getOrgNoTargetDatesEnabled(orgId);
    return result?.no_target_dates_enabled === true;
  } catch {
    return false;
  }
};

/**
 * Upload the patient's just-approved care plan if their clinic auto-pushes.
 *
 * Never throws: the approval has already succeeded by the time this runs, so the caller must
 * be able to report "approved, but not uploaded" rather than have an upload error read as a
 * failed approval.
 *
 * Must be called AFTER every post-approve write that changes the document — notably the
 * review (expiration) date — or the filed PDF carries the value from before that write.
 */
export const autoPushCarePlanToEhr = async (
  patientId: string,
  options: { organizationId?: string | null } = {},
): Promise<AutoPushOutcome> => {
  const enabled = await resolveAutoPushEnabled(patientId);
  if (enabled === false) return { status: 'skipped' };
  if (enabled === null) {
    // We could not learn whether this clinic auto-pushes. Most clinics do not, so claiming a
    // failed upload here would be wrong far more often than right; the manual button on the
    // chart remains available either way.
    return { status: 'skipped' };
  }

  try {
    const patient = await patientApi.get(patientId, options.organizationId || undefined);
    const definitions: any[] = Array.isArray(patient?.care_plan_definitions)
      ? patient.care_plan_definitions
      : [];
    // Never file a "No care plans defined" document into a medical record: if the approved
    // plan is not visible yet, that is an upload failure the clinician should hear about.
    if (definitions.length === 0) {
      throw new Error('PHA-1204: no approved care plan found to upload');
    }

    const orgId = patient?.organization_id || patient?.organization || null;
    const [voiceOfPatientEnabled, noTargetDatesEnabled, carePlanReviews] = await Promise.all([
      resolveVoiceOfPatientEnabled(orgId ? String(orgId) : null),
      resolveNoTargetDatesEnabled(patient, orgId ? String(orgId) : null),
      // Fetched fresh at upload time; a failure files the plan without notes (PHA-1204).
      fetchCarePlanReviewsForPdf(patientId),
    ]);

    const payload = await buildCarePlanPdfPayload({
      patient,
      carePlanDefinitions: definitions,
      hasApprovedCarePlans: definitions.length > 0,
      voiceOfPatientEnabled,
      noTargetDatesEnabled,
      carePlanReviews,
      // Suppresses the PHA-942 "printed and handed" note and keeps the copy in English (PHA-1195).
      isEhrCopy: true,
    });
    const { blob, filename } = await generateCarePlanPdfBlob(payload);
    await patientApi.pushCarePlanToEhr(patientId, blob, filename, false, true);
    return { status: 'submitted' };
  } catch (err: any) {
    // 409: this exact plan version is already in the chart. The button turns that into a
    // "send another copy?" prompt; an automatic upload must never file a duplicate on its own.
    if (err?.alreadySent) return { status: 'already_sent' };
    console.error('PHA-1204: automatic care plan upload to the EHR failed:', err);
    return { status: 'failed', error: err };
  }
};
